/* eslint-disable eqeqeq */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import SearchableSelect from 'pages/project-board/components/ahus/SearchableSelect';
import type { IDuplicateProjectRow } from 'models';
import { readSizerAirflow } from '../sizerStorage';
import SizerFloatingIcon from './SizerFloatingIcon';
import { POPUP_STATES } from './sizerPopupStorage';
import type { SizerSelectionController } from './sizerSelectionController';
import { formatSpan } from './legacyFormatter';

/**
 * Phase 4e — the AHU SELECTION POPUP, `header/tmpl/sizer_popup.tmpl` (382 lines)
 * driven by `header/view/sizer_popup.js` (1217). Opened by a matrix-cell click
 * (`sizer/mvc/view/main.js:596-637`) and the owner of the only server writes in
 * the `/sizer/` feature.
 *
 * ★★ SCOPE — HEADER-OWNED, exactly like legacy.
 *
 * The logic lives here, under `pages/sizer/`; the markup is rendered by
 * `GlobalHeader` into its `<div id="sizer_popup">`, so the DOM tree, the ancestor
 * chain and the CSS are byte-identical to legacy. The controller is an SPA-wide
 * singleton (`sizerSelectionHost.ts`) — the React shape of `header.js:203-215`
 * building `new SizerPopup(...)` on every page that has a logged-in user.
 *
 * WHY THE DOM HAS TO BE INSIDE `#sizer_popup`: `.on-sizer-page`
 * (`sizer_popup.js:147`) and `.on-ahu-page` (`:148`) are classes on THAT node and
 * they position BOTH floating icons (`_sizer.content.scss:397-405`,
 * `common.css:11340-11346`), including the
 * `.floating-icon + #rating_floating-icon` sibling shift — so the sizer icon has
 * to render BEFORE the Ratings icon, exactly as legacy's `minimize()` +
 * `renderFloatingIcon()` ordering does (`sizer_popup.js:743` then
 * `header.js:560-570`).
 *
 * ⚠ 4e SCOPED THIS TO `/sizer/`, AND THAT NARROWING IS WHAT QA REPORTED. A
 * page-scoped controller meant no window on the AHU board / dashboard / project
 * board, no floating-icon restore there, and nothing listening for the `storage`
 * events a `/sizer/` tab fires — three symptoms of one gap. They came back
 * together when the controller became header-owned. It is not expensive on the
 * boards that never size: every loader early-returns unless the `sizer_popup`
 * key exists (`sizerPopupApi.ts:201`, `:214`), so no `/sizer/` state ⇒ no
 * requests and nothing to restore.
 *
 * STILL NOT PORTED: `header.js:418-463`, the socket-driven refresh of the
 * POPUP's own project/AHU lists when ANOTHER user edits them. (Its mute flag
 * `sizer_doesnt_need_update` is written and read, so a legacy tab and this one
 * agree.) The AHU BOARD's half of that fan-out — the tunnel W/H + canvas refresh
 * after a sizer APPLY — is in `pages/ahu-board/hooks/useAhuBoardSyncdata.ts`.
 */

export interface SizerSelectionPopupProps {
  controller: SizerSelectionController;
}

/** jQuery UI `draggable`'s default `cancel` option — a drag never starts on a
 * form control, which is what keeps the selects usable inside the drag surface. */
const DRAG_CANCEL = 'input,textarea,button,select,option';
/** jQuery UI `draggable`'s default `distance` — a drag only begins after 1px. */
const DRAG_DISTANCE = 1;
/** How much of the popup a restore must leave on screen, so its drag handle is
 * always reachable even if the saved position came from a larger window. */
const MIN_VISIBLE = 60;

/** A stored `draggable-popup` coordinate, which legacy writes as a number but
 * whose type allows a CSS string, as a page-coordinate number. */
const toRestoredCoord = (value: number | string | undefined, fallback: number): number => {
  const parsed = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const SizerSelectionPopup = ({ controller }: SizerSelectionPopupProps) => {
  useSyncExternalStore(controller.subscribe, controller.getVersion, controller.getVersion);

  const draggableRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const successTimer = useRef<number | null>(null);
  const [successVisible, setSuccessVisible] = useState(false);

  // ---- `__successfulApply` (sizer_popup.js:997-1003) + the explicit hide at
  // :887. Note the banner is shown BEFORE the first request goes out and is
  // hidden the moment the last one resolves — or after 3 s, whichever is first.
  useEffect(() => {
    controller.showSuccess = () => {
      setSuccessVisible(true);
      if (successTimer.current) window.clearTimeout(successTimer.current);
      successTimer.current = window.setTimeout(() => setSuccessVisible(false), 3000);
    };
    controller.hideSuccess = () => setSuccessVisible(false);
    return () => {
      controller.showSuccess = undefined;
      controller.hideSuccess = undefined;
      if (successTimer.current) window.clearTimeout(successTimer.current);
    };
  }, [controller]);

  // ---- the popup's OWN Esc handler (sizer_popup.js:143-146). Distinct from the
  // page's blanket Esc (main.js:150-160): this one is a full `close_popup()`.
  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && controller.state === POPUP_STATES.OPENED) controller.closePopup();
    };
    document.addEventListener('keyup', onKeyUp);
    return () => document.removeEventListener('keyup', onKeyUp);
  }, [controller]);

  /**
   * `$("#draggable-popup").draggable({drag: …})` (:469-477) + the restore at
   * :452-468. jQuery UI writes inline `left`/`top` deltas on a
   * `position:relative` element (`.animate` is `position: relative !important`,
   * `_sizer.content.scss:104`) and persists `$(this).offset()` — the PAGE
   * coordinates. Legacy's mismatch between what it writes here and what it reads
   * back on open is fixed on the READ side; see the restore effect below.
   */
  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const el = draggableRef.current;
      if (!el || e.button !== 0) return;
      const target = e.target as Element | null;
      if (target?.closest?.(DRAG_CANCEL)) return;
      const style = window.getComputedStyle(el);
      const startX = e.clientX;
      const startY = e.clientY;
      const baseLeft = parseFloat(style.left) || 0;
      const baseTop = parseFloat(style.top) || 0;
      let started = false;

      /**
       * ★ DOCUMENT LISTENERS + A DISTANCE THRESHOLD, NOT `setPointerCapture` —
       * this matters for correctness, not style. jQuery UI's `draggable` binds
       * `mousemove`/`mouseup` on the DOCUMENT and honours `distance: 1`, so a
       * plain click on anything inside the popup is untouched. Pointer capture
       * would retarget the compatibility `click` event to the capturing element,
       * and every non-`cancel` descendant (the project dropdown's `<li>`s, the
       * component rows) would stop being clickable. MEASURED: with capture, the
       * project dropdown selected nothing at all.
       */
      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== e.pointerId) return;
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        if (!started && Math.abs(dx) < DRAG_DISTANCE && Math.abs(dy) < DRAG_DISTANCE) return;
        started = true;
        el.style.left = `${baseLeft + dx}px`;
        el.style.top = `${baseTop + dy}px`;
        // :470-476 — persisted on EVERY drag tick, as PAGE offsets.
        const rect = el.getBoundingClientRect();
        controller.persistDraggablePosition({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          'z-index': 'unset',
        });
      };
      const onUp = () => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
      };
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onUp);
    },
    [controller]
  );

  /**
   * The restore, once per open (:452-468) — ★ CORRECTED, not reproduced.
   *
   * `drag` persists `$(this).offset()`, i.e. PAGE coordinates. Legacy fed those
   * straight back into `css({top,left})` on a `position:relative` element, where
   * `top`/`left` are OFFSETS from the element's natural flow position — so the
   * popup reopened one natural offset BELOW where it was dropped (the
   * `top: calc(50% - 400px)` on `.selection-content`, `_sizer.content.scss:99`,
   * plus any document scroll at drag time). That is the reported "after a
   * refresh the dialog is at the bottom of the page".
   *
   * The stored FORMAT is unchanged — page coordinates, so a legacy tab writing
   * the same `draggable-popup` key still round-trips — we just convert them into
   * the offsets that actually land the popup there: clear the inline offsets,
   * measure where the popup naturally sits in those same page coordinates, and
   * set the difference. A drag followed by a refresh now returns the popup to
   * exactly where it was dropped.
   */
  const restoredFor = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (controller.state !== POPUP_STATES.OPENED) {
      restoredFor.current = null;
      return;
    }
    if (restoredFor.current != null) return;
    const el = draggableRef.current;
    const position = controller.draggablePosition;
    if (!el || !position) return;
    restoredFor.current = 1;
    if (contentRef.current) {
      contentRef.current.style.marginTop = '0'; // :464-467
      contentRef.current.style.marginLeft = '0';
    }
    el.style.top = '';
    el.style.left = '';
    /**
     * ★ THE `easePopup` ANIMATION HAS TO BE OFF TO MEASURE. `.animate-transition`
     * (`_sizer.content.scss:148`) runs `easePopup` (`_ui.popups.scss:877-886`),
     * whose 0% frame is `transform: scale(0, 0)` — and `getBoundingClientRect()`
     * reports the TRANSFORMED box. Measured mid-animation the popup reads as a
     * zero-size box at its own centre, i.e. half its height too low and half its
     * width too far right, so the correction overshoots by exactly that and the
     * popup lands off the top-left corner of the screen. Suppressing the
     * animation with an inline shorthand (which beats the class in the cascade,
     * where a plain inline `transform` would lose to the running animation) gives
     * the real layout box. Cleared again below, before any paint, so the popup
     * still animates open normally.
     */
    el.style.animation = 'none';
    const rect = el.getBoundingClientRect();
    el.style.animation = '';
    const naturalTop = rect.top + window.scrollY;
    const naturalLeft = rect.left + window.scrollX;
    // Clamped in VIEWPORT space: the popup sits inside a `position:fixed`
    // wrapper, so a position saved on a bigger window must not push the header
    // bar out of reach on a smaller one.
    const width = rect.width || el.offsetWidth;
    const top = Math.min(
      Math.max(toRestoredCoord(position.top, naturalTop) - window.scrollY, 0),
      Math.max(window.innerHeight - MIN_VISIBLE, 0)
    );
    const left = Math.min(
      Math.max(toRestoredCoord(position.left, naturalLeft) - window.scrollX, MIN_VISIBLE - width),
      Math.max(window.innerWidth - MIN_VISIBLE, 0)
    );
    el.style.top = `${top + window.scrollY - naturalTop}px`;
    el.style.left = `${left + window.scrollX - naturalLeft}px`;
  });

  // ---- SearchableSelect wiring (sizer_popup.js:121-128, :593-598) -----------
  const projectRows = useMemo(
    () => controller.projectList as unknown as IDuplicateProjectRow[],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controller.projectList]
  );
  const selectedProjectId = controller.fieldsStatus.project_select_value;

  // ---- render --------------------------------------------------------------

  if (controller.state === POPUP_STATES.MINIMIZED) {
    /**
     * `minimize()` (:735-751) replaces the whole `#sizer_popup` content with
     * `header/tmpl/sizer_floating_icon.tmpl` (4 lines) and `.draggable()`s it
     * (:745). Clicking it re-opens (`events`: `click #floating-icon:
     * open_popup`, :40). Both live in `SizerFloatingIcon`; the icon is
     * `position:fixed` off `.on-sizer-page` (`_sizer.content.scss:348-405`),
     * which is what places it until it is dragged.
     */
    return <SizerFloatingIcon onOpen={() => void controller.openPopup()} />;
  }

  if (controller.state !== POPUP_STATES.OPENED) return null;

  const data = controller.data;
  if (!data) {
    // :525-529 — `console.warn('No valid data for sizer popup')` + close.
    return null;
  }

  const airflow = readSizerAirflow(); // :536, :539
  const coolingCoil = (data.cooling_coil ?? {}) as Record<string, unknown>;
  const heatingCoil = (data.heating_coil ?? {}) as Record<string, unknown>;
  const filter = (data.filter ?? {}) as Record<string, unknown>;

  /** The `<select class="section_select" id="section_N_select">` every row ends
   * with — options from `__render_sections` (:666-722), value from the bag. */
  const renderSectionSelect = (index: number) => (
    <div className="col-xs-2 pl-35 top-2 width-200">
      <select
        className="section_select"
        id={`section_${index}_select`}
        value={controller.sectionValue(index)}
        onChange={(e) => controller.syncChange(`section_${index}_select`, e.target.value)}
      >
        {controller.sectionOptions(index).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  /** A Cooling/Heating Coil row (sizer_popup.tmpl:135-160 and its 5 copies). */
  const renderCoilRow = (
    index: number,
    labelFor: string,
    title: string,
    coil: Record<string, unknown>
  ) => (
    <label key={index} htmlFor={labelFor} className="col-xs-12 components-part__items">
      <div className="col-xs-2 width-130">
        <div className="left" />
        <div className="left  change-color">{title}</div>
      </div>
      <div className="col-xs-8 row width--350">
        <div className="col-xs-1 text-uppercase change-color text-right p-0">
          {coil.qty as never}
        </div>
        <div className="col-xs-3 text-uppercase change-color text-right">
          {coil.fh as never} <span className="text-lowercase">x</span> {coil.fl as never}
        </div>
        <div className="col-xs-3" />
        <div className="col-xs-3 text-uppercase change-color text-right">
          {coil.face_area as never}
        </div>
        <div className="col-xs-2 text-uppercase change-color text-right no-wrap">
          {coil.face_velocity as never}
        </div>
      </div>
      {renderSectionSelect(index)}
    </label>
  );

  /** A Filter row (sizer_popup.tmpl:294-317 and its 2 copies). The first two
   * data cells are EMPTY and the third — which the coil rows leave blank — holds
   * `size_qty` and drops `text-right`. */
  const renderFilterRow = (index: number) => (
    <label key={index} htmlFor="filter" className="col-xs-12 components-part__items">
      <div className="col-xs-2 width-130">
        <div className="left" />
        <div className="left  change-color">Filter</div>
      </div>
      <div className="col-xs-8 row width--350">
        <div className="col-xs-1 text-uppercase change-color text-right p-0" />
        <div className="col-xs-3 text-uppercase change-color text-right" />
        <div className="col-xs-3 text-uppercase change-color">{filter.size_qty as never}</div>
        <div className="col-xs-3 text-uppercase change-color text-right">
          {filter.face_area as never}
        </div>
        <div className="col-xs-2 text-uppercase change-color text-right no-wrap">
          {filter.face_velocity as never}
        </div>
      </div>
      {renderSectionSelect(index)}
    </label>
  );

  return (
    /* sizer_popup.tmpl:1 — the root class list is literal, `active` included; the
       ONLY thing that ever removes it is the page's backdrop handler. */
    <div
      className={`sizer sizerSelection popup-wrapper${controller.backdropDismissed ? '' : ' active'}`}
    >
      <div className="selection-content" id="popup_setion_content" ref={contentRef}>
        <div
          className={`col-xs-12 animate  ui-widget-content${
            controller.animateTransition ? ' animate-transition' : ''
          } ui-draggable ui-draggable-handle`}
          id="draggable-popup"
          ref={draggableRef}
          onPointerDown={onPointerDown}
        >
          <div className="col-xs-12 popup-header row">
            <div className="">
              <div className="left col-xs-11">
                <h3 className="text-uppercase sizer-header__title">Sizer</h3>
                <span className="text-lowercase header__text">air handling unit</span>
              </div>
              {/* `click .minimize-icon: minimize` (:45). ⚠ The sizer PAGE binds the
                  same selector to a handler it never defines (main.js:51) —
                  Backbone skips undefined handlers, so only this one runs. */}
              <div
                className="right col-xs-1 minimize-icon header__icon"
                onClick={() => controller.minimize()}
              >
                <i className="right icon-remove " />
              </div>
            </div>
          </div>

          <div className="col-xs-12 e-stop">
            <div className="popup-content__header">
              <div className="col-xs-12 project-part">
                <div className="label col-xs-1">Project</div>
                <div className="col-xs-11 project-part__select">
                  <SearchableSelect
                    id="project_select"
                    none
                    list={projectRows}
                    name={String(controller.fieldsStatus.project_select_name ?? '--')}
                    selectedId={selectedProjectId ? Number(selectedProjectId) : null}
                    onSearch={(value) => void controller.searchProjects(value)}
                    onScrollEnd={() => void controller.scrollProjects()}
                    onSelect={(id, name) => void controller.changeProject(id, name ?? '--')}
                  />
                </div>
              </div>
              <div className="col-xs-12">
                <div className="label col-xs-1">AHU</div>
                <div className="col-xs-8" style={{ paddingLeft: '34px' }}>
                  <select
                    className="ahuName col-xs-4"
                    name="ahu_name"
                    id="ahu_select"
                    value={controller.ahuValue}
                    onChange={(e) => void controller.changeAHU(e.target.value)}
                  >
                    {/* `__render_ahus` (:604) always leads with `--`. */}
                    <option value="">--</option>
                    {controller.ahuOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                {/* :32-34 — permanently `.hidden`; the id collides with the
                    matrix's own `#cad_simple_preloader` in legacy too. */}
                <div className="section_loader col-xs-1 hidden">
                  <div id="cad_simple_preloader" className="" />
                </div>
                <div className="col-xs-3 right d-flex align-items-center">
                  {airflow ? (
                    <div className="airflow-label-wrapper">
                      <div className="label airflow-label">Supply Airflow</div>
                      <div className="d-flex">
                        {/* `data-formatter_type="integer"` → `bindFormatter`
                            (:580-587) → "20,000". */}
                        <span className="airflow-output" data-formatter_type="integer">
                          {formatSpan(airflow, 'integer')}
                        </span>
                        <span className="label pl-5">CFM</span>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="col-xs-12 sizer-pop-up-content e-stop">
            <div
              className={`col-xs-12 popup-content__body${
                controller.selectionActive ? '' : ' non-clickable'
              }`}
            >
              <div className="row cabinet-part">
                <div className="col-xs-12 cabinet-part__title">
                  <div className="col-xs-4 text-uppercase">TUNNEL CONFIGURATION</div>
                  <div className="col-xs-4">
                    CURRENT OD W x H <span className="text-lowercase">(in)</span>
                  </div>
                  <div className="col-xs-4">
                    SUGGESTED OD W x H<span className="text-lowercase"> (in)</span>
                  </div>
                </div>
                <div className="col-xs-12 cabinet-part__info">
                  {/* `__render_tunnels` (:614-664) builds these three columns from
                      the SAME tunnel list, so the rows line up positionally. */}
                  <div className="col-xs-4">
                    <div className="row" id="tunnel_data">
                      {controller.tunnels.map((tunnel) => (
                        <div className="col-xs-12 tunnel-item" key={tunnel.id}>
                          {controller.tunnelIndexesMapping[tunnel.index_id]?.name}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="col-xs-4">
                    <div className="row" id="currentCabinetData">
                      {controller.tunnels.map((tunnel) => (
                        <div className="col-xs-12 tunnel-item" key={tunnel.id}>
                          {' '}
                          <span data-formatter_type="decimal">
                            {formatSpan(tunnel.width, 'decimal')}
                          </span>{' '}
                          x{' '}
                          <span data-formatter_type="decimal">
                            {formatSpan(tunnel.height, 'decimal')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="col-xs-4" id="matrix_recomended">
                    {controller.tunnels.map((tunnel) => (
                      <div className="row tunnel-item" key={tunnel.id}>
                        <div className="col-xs-1">
                          <label
                            className="tunnel_checkbox_label"
                            htmlFor={`tunnel_${tunnel.id}_checkbox`}
                          />
                          <input
                            type="checkbox"
                            id={`tunnel_${tunnel.id}_checkbox`}
                            className="tunnel_checkbox"
                            checked={controller.isTunnelChecked(tunnel.id)}
                            onChange={(e) =>
                              controller.syncChange(
                                `tunnel_${tunnel.id}_checkbox`,
                                e.target.checked
                              )
                            }
                          />
                        </div>
                        <div className="col-xs-10">
                          {data.current_width as never}
                          {'  x '}
                          {data.current_height as never}{' '}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col-xs-12">
                  <hr />
                </div>
                <div className="components-part-body">
                  <div className="col-xs-12 components-part">
                    <div className="col-xs-2 width-130 text-uppercase change-color left">
                      Component
                    </div>
                    <div className="col-xs-8 width--350 row">
                      <div className="col-xs-1 text-uppercase change-color text-right">QTY</div>
                      <div className="col-xs-3 text-uppercase change-color text-right">
                        FH <span className="text-lowercase">x</span> FL
                      </div>
                      <div className="col-xs-3 text-uppercase change-color no-wrap">
                        BANK WIDE <span className="text-lowercase">x</span> HIGH
                      </div>
                      <div className="col-xs-3 text-uppercase change-color text-right no-wrap">
                        FACE AREA{' '}
                        <span className="text-lowercase">
                          (ft<sup>2</sup>)
                        </span>
                      </div>
                      <div className="col-xs-2 text-uppercase change-color text-right no-wrap">
                        VELOCITY <span className="text-lowercase">(fpm)</span>
                      </div>
                    </div>
                    <div className="col-xs-2 width-200 change-color no-wrap pl-35">
                      TUNNEL : SECTION : TAG
                    </div>
                  </div>

                  {/* ⚠ NOT PORTED — `<form id="Fan" class="hide">`
                      (sizer_popup.tmpl:92-133). Permanently hidden, renders no
                      `.section_select`, and its `INDEX_FIELD_MAPPING` entries 1-3
                      are commented out upstream, so nothing can ever select a fan
                      section. Same call the settings panel made for its hidden
                      duplicate block (discovery §6c). */}

                  <form action="" id="Coil">
                    {[3, 4, 5].map((index) =>
                      // 'coiling_coil' [sic] — the template's own typo, on all three.
                      renderCoilRow(index, 'coiling_coil', 'Cooling Coil', coolingCoil)
                    )}
                    {[6, 7, 8].map((index) =>
                      renderCoilRow(index, 'heating_coil', 'Heating Coil', heatingCoil)
                    )}
                  </form>
                  <form action="" id="Filter">{[9, 10, 11].map((index) => renderFilterRow(index))}</form>
                  <div />
                </div>
              </div>
            </div>

            <div className="popup-content-footer col-xs-12">
              <div className="popup-control-buttons">
                <span id="successful_apply" className={successVisible ? '' : 'hide'}>
                  Applied...
                </span>
                {/* ⚠ `#failed_apply` can never be shown — `__failedApply` is
                    unreachable (`apply`'s catch TypeErrors first). See
                    `sizerSelectionController.apply`. Rendered for DOM parity. */}
                <span id="failed_apply" className="hide">
                  Failed...
                </span>
                <button
                  className="btn text-uppercase done right"
                  type="submit"
                  name="cancel"
                  onClick={() => controller.closePopup()}
                >
                  close
                </button>
                <button
                  /* The template writes `right {{#unless confirmation_state}}
                     non-clickable {{/unless}}` (:378), but `__check_confirmation_state`
                     immediately re-derives it through `classList.add/remove`
                     (:416-420) — which re-serialises the attribute NORMALISED.
                     OBSERVED on legacy: exactly `btn text-uppercase right`
                     enabled, `… right non-clickable` disabled. Matched. */
                  className={`btn text-uppercase right${
                    controller.confirmationState ? '' : ' non-clickable'
                  }`}
                  name="apply"
                  value="apply"
                  type="button"
                  onClick={() => void controller.apply()}
                >
                  apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SizerSelectionPopup;
